import React from "react";
import { api, IMAGES, GALLERY_CATEGORIES, fileUrl } from "@/lib/config";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const FALLBACK = {
  kitchens: [IMAGES.kitchen],
  bathrooms: [IMAGES.bathroom],
  living_spaces: [IMAGES.living],
  custom_tile: [IMAGES.custom],
};

export default function Gallery() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.get("/gallery")
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const byCategory = (cat) => items.filter((i) => i.category === cat);

  return (
    <div data-testid="gallery-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="max-w-3xl">
        <div className="eyebrow text-[color:var(--brand-wood)]">Gallery</div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          Our work, up close.
        </h1>
        <p className="mt-4 text-[color:var(--brand-woodDark)]">
          A selection of recent flooring and tile projects across Florida and neighboring areas.
        </p>
      </div>

      <Tabs defaultValue="all" className="mt-12" data-testid="gallery-tabs">
        <TabsList className="bg-transparent border border-black/10 rounded-full p-1 h-auto flex flex-wrap">
          <TabsTrigger data-testid="gallery-tab-all" value="all" className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]">All</TabsTrigger>
          {GALLERY_CATEGORIES.map((c) => (
            <TabsTrigger
              key={c.key}
              data-testid={`gallery-tab-${c.key}`}
              value={c.key}
              className="rounded-full px-5 py-2 text-xs data-[state=active]:bg-[color:var(--brand-ink)] data-[state=active]:text-[color:var(--brand-cream)]"
            >
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="all" className="mt-8">
          <Grid items={items} loading={loading} />
        </TabsContent>
        {GALLERY_CATEGORIES.map((c) => (
          <TabsContent key={c.key} value={c.key} className="mt-8">
            <Grid items={byCategory(c.key)} loading={loading} fallbackKey={c.key} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function Grid({ items, loading, fallbackKey }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="aspect-[4/3] rounded-2xl bg-black/5 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!items.length) {
    const fallbacks = fallbackKey ? FALLBACK[fallbackKey] : Object.values(FALLBACK).flat();
    return (
      <div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {fallbacks.map((src, i) => (
            <img key={i} src={src} alt="Gallery preview" className="aspect-[4/3] w-full rounded-2xl object-cover"/>
          ))}
        </div>
        <p className="mt-6 text-sm text-[color:var(--brand-woodDark)]">
          Showing sample previews. New projects are added continuously.
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {items.map((it, i) => (
        <figure
          key={it.id}
          data-testid={`gallery-item-${i}`}
          className="group relative aspect-[4/3] rounded-2xl overflow-hidden hover-lift"
        >
          <img src={fileUrl(it.image_path)} alt={it.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
          <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white">
            <div className="font-display text-lg leading-tight">{it.title}</div>
            {it.description && <div className="text-xs opacity-80 mt-0.5">{it.description}</div>}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
